import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, ArrowLeft } from "lucide-react";

/**
 * Two-step 2FA: arrives here from /login when backend returned 401 + "2FA code required".
 * We keep the email/password in `sessionStorage` (cleared on success/cancel) so the
 * user only sees the OTP step, then we re-issue /auth/login with `otp`.
 */
export default function TwoFAVerify() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [creds] = useState(() => {
    try {
      const raw = sessionStorage.getItem("pending_2fa");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!creds) navigate("/login", { replace: true });
  }, [creds, navigate]);

  const cancel = () => {
    sessionStorage.removeItem("pending_2fa");
    navigate("/login", { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!creds) return;
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", {
        email: creds.email,
        password: creds.password,
        otp: otp.replace(/\s+/g, ""),
      });
      setUser(data);
      sessionStorage.removeItem("pending_2fa");
      const to = data.role === "candidate" ? "/candidate" : data.role === "owner" ? "/admin" : "/recruiter";
      navigate(to, { replace: true });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-12">
      <div className="col-span-12 lg:col-span-7 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <button onClick={cancel} className="inline-flex items-center gap-1 text-sm font-semibold hover:text-[var(--brand-primary)] mb-6" data-testid="twofa-back">
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </button>
          <div className="label-eyebrow flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> Two-factor authentication</div>
          <h1 className="font-display font-black text-5xl tracking-tight mt-3">Enter code.</h1>
          <p className="text-[#5C5C5C] mt-3">
            Open your authenticator app and enter the 6-digit code for <strong>{creds?.email}</strong>.
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5">
            <div>
              <label className="label-eyebrow block mb-2">6-digit code</label>
              <input
                data-testid="twofa-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9 ]*"
                required
                autoFocus
                maxLength={9}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="w-full border border-[#0F0F0F] bg-transparent px-4 py-3 rounded-sm font-mono text-2xl tracking-[0.5em] text-center"
                placeholder="000 000"
              />
            </div>
            {error && <div data-testid="twofa-error" className="text-sm text-[var(--brand-primary)] font-medium">{error}</div>}
            <button data-testid="twofa-submit" disabled={loading || otp.length < 6} type="submit" className="btn-terra w-full text-base">
              {loading ? "Verifying…" : "Verify & sign in"}
            </button>
          </form>

          <p className="mt-10 text-sm text-[#5C5C5C]">
            Lost your authenticator?{" "}
            <Link to="/forgot-password" className="font-semibold text-[#0F0F0F] underline underline-offset-4 hover:text-[var(--brand-primary)]">
              Reset by email
            </Link>
            .
          </p>
        </div>
      </div>
      <div className="hidden lg:block lg:col-span-5 bg-[#0F0F0F] relative">
        <div className="absolute inset-0 opacity-50" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1573165706511-3ffde6ef1fe3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0F0F0F]/95 via-[#0F0F0F]/70 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-[#FDFCF8]">
          <div className="label-eyebrow" style={{ color: "var(--brand-primary)" }}>Secured</div>
          <h2 className="font-display font-black text-4xl mt-3 leading-tight">
            Two-factor keeps your hiring pipeline private.
          </h2>
          <p className="text-sm text-[#c2c2c2] mt-4">— Ocean Ash Ltd Security</p>
        </div>
      </div>
    </div>
  );
}
